import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import NameEditComponent from '../NameEditComponent/NameEditComponent';
import styles from './NewStoryboardCreator.module.scss';
import { CustomButton } from '../Button/Button';
import Icon from '../Icon/Icon';
import TextWithInfo from '../TextWithInfo/TextWithInfo';
import SelectOption from '../../components/SelectOption/SelectOption';
import cx from 'classnames';
import VideoService from '../../service/VideoService';
import Spinner from '../../../common/components/Spinner';
import CloseButton from '../CloseButton/CloseButton';
import Modal from '../Modal';
import queryString from 'query-string';

import { ORIENTATIONS, LANGUAGES, VOICE_TONES, NUMBERS_OF_ACTORS } from '../../constants/projectCreatorConsts';
import TemplateItem from '../TemplateItem/TemplateItem';
import { useStoryboardContext } from 'engage/context/StoryboardContext';

const MIN_ANSWER_LENGTH = 5;
const MAX_ANSWER_LENGTH = 1000;

const MAX_SCENES = 6;
const DEFAULT_SCENES = 3;

const generalCategory = { id: 'all', title: 'All', key: 'storyboard.generating.templates.all' };

const NewStoryboardCreator = ({
  changeOrientation,
  generateNarratives,
  skipGenerateNarratives,
  selectedOrientation,
}) => {
  const { t, i18n } = useTranslation();

  const { projectName, updateProjectName, generationRequest } = useStoryboardContext();

  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language === 'nl' ? LANGUAGES.NEDERLANDS : LANGUAGES.ENGLISH);
  const [selectedVoiceTone, setSelectedVoiceTone] = useState(VOICE_TONES.CASUAL);

  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const [numberOfActors, setNumberOfActors] = useState(NUMBERS_OF_ACTORS.ONE);

  const scenesNumberOptions = useMemo(() => {
    const result = {};
    for (let i = 1; i <= MAX_SCENES; i++) {
      result[i] = { name: `${i}`, type: i };
    }

    return result;
  }, [MAX_SCENES]);

  const [scrolled, setScrolled] = useState(false);
  const [numberOfScenes, setNumberOfScenes] = useState(scenesNumberOptions[DEFAULT_SCENES]);

  const [isTemplatesLoading, setIsTemplatesLoading] = useState(true);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableTemplates, setAvailableTemplates] = useState([]);

  const [templateAnswers, setTemplateAnswers] = useState([]);

  const [isAdvancedOpened, setIsAdvancedOpened] = useState(false);
  const [isShowQuestions, setIsShowQuestions] = useState(false);

  const [touchedAnswers, setTouchedAnswers] = useState([]);

  const templatesWrapper = useRef(null);

  const templatesToDisplay = useMemo(
    () =>
      availableTemplates?.filter(
        ({ categories }) => categories?.some(({ id }) => id === selectedCategoryId) || selectedCategoryId === 'all'
      ),
    [availableTemplates, selectedCategoryId]
  );

  const questionsToDisplay = useMemo(() => availableTemplates?.find(({ id }) => id === selectedTemplateId)?.fields || [], [
    availableTemplates,
    selectedTemplateId,
  ]);

  const isValid = useMemo(
    () =>
      selectedOrientation.type &&
      selectedLanguage.type &&
      selectedVoiceTone.type &&
      selectedTemplateId &&
      numberOfActors.type &&
      numberOfScenes.type,
    [selectedOrientation, selectedLanguage, selectedVoiceTone, selectedTemplateId, numberOfActors, numberOfScenes]
  );

  const isTemplateAnswersValid = useMemo(
    () =>
      templateAnswers.every(({ answer }) => answer?.trim()?.length > MIN_ANSWER_LENGTH) &&
      questionsToDisplay?.length === templateAnswers?.length &&
      templateAnswers?.length > 0,
    [templateAnswers, questionsToDisplay]
  );

  const history = useHistory();
  const location = useLocation();

  const isExistingStory = useMemo(() => {
    const parsed = queryString.parse(location.search);
    return parsed.videoId != null;
  }, [location]);

  const handleToggleAdvancedOpened = () => {
    setIsAdvancedOpened(prevState => {
      setTimeout(() => {
        window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
      }, 100);
      return !prevState;
    });
  };

  const handleScroll = e => {
    if (window.scrollY > 1) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChangeAnswer = (event, id) => {
    event.persist();

    setTouchedAnswers(prevState => {
      if (prevState.includes(id)) {
        return prevState;
      }

      return [...prevState, id];
    });

    setTemplateAnswers(prevState => {
      const isAnswerExist = prevState.find(answer => answer?.id === id);

      if (isAnswerExist) {
        return prevState.map(answer => {
          if (answer.id === id) {
            return { id, answer: event.target?.value?.trimStart() };
          }

          return answer;
        });
      } else {
        return [...prevState, { id, answer: event.target?.value?.trimStart() }];
      }
    });
  };

  const handleNextStep = async () => {
    setIsShowQuestions(true);
  };

  const onGenerate = useCallback(() => {
    const payload = {
      orientation: selectedOrientation.type,
      voiceTone: selectedVoiceTone.type,
      numberOfActors: numberOfActors.type,
      numberOfScenes: numberOfScenes.type,
      templateId: selectedTemplateId,
      fields: templateAnswers,
    };
    generateNarratives(payload);
  }, [selectedOrientation, selectedVoiceTone, numberOfActors, numberOfScenes, selectedTemplateId, templateAnswers, generateNarratives]);

  const returnKeyByValueType = (obj, request) => {
    const [key] = Object.entries(obj).find(([_, value]) => request === value.type);
    if (key) {
      return obj[key];
    }
    return null;
  };

  const handleSelectLanguage = useCallback(
    value => {
      if (generationRequest) {
        if (generationRequest.template.language === value.type && generationRequest.template.id === selectedTemplateId) {
          setTemplateAnswers(generationRequest.request.fields);
          setSelectedLanguage(value);
          return;
        }
      }
      setTemplateAnswers([]);
      setSelectedLanguage(value);
    },
    [generationRequest, selectedTemplateId]
  );

  const handleSelectTemplate = useCallback(
    id => {
      if (generationRequest) {
        if (generationRequest.template.id === id) {
          setTemplateAnswers(generationRequest.request.fields);
          setSelectedTemplateId(id);
          return;
        }
      }
      if (selectedTemplateId !== id) {
        setTemplateAnswers([]);
        setTouchedAnswers([]);
        setSelectedTemplateId(id);
      }
    },
    [generationRequest, selectedTemplateId]
  );

  useEffect(() => {
    const getTemplates = async () => {
      try {
        setIsTemplatesLoading(true);
        const templates = await VideoService.getStoryboardTemplates({ language: selectedLanguage.type }).catch(err =>
          console.log(err)
        );

        if (templates?.length) {
          const customTemplates = templates.filter(({ companyId }) => companyId);
          const customOrDefaultTemplates = customTemplates?.length ? customTemplates : templates;

          setAvailableTemplates(customOrDefaultTemplates);

          const filteredCategories = customOrDefaultTemplates.reduce((acc, { categories }) => {
            const uniqueCategories = categories?.filter(
              newCategory => !acc.some(existingCategory => existingCategory.id === newCategory.id)
            );
            return [...acc, ...uniqueCategories];
          }, []);

          setAvailableCategories([generalCategory, ...filteredCategories]);
        } else {
          setAvailableTemplates([]);
          setAvailableCategories([generalCategory]);
        }

        setSelectedCategoryId('all');

        if (generationRequest && generationRequest.template.language === selectedLanguage.type) {
          const currentTemplate = templates.find(({ id }) => id === generationRequest.template.id);

          setSelectedCategoryId(currentTemplate?.categories[0]?.id || 'all');
          setSelectedTemplateId(currentTemplate?.id);
        } else {
          setSelectedTemplateId(null);
        }
      } catch (err) {
        console.log(err);
      }

      setIsTemplatesLoading(false);
    };

    selectedLanguage && getTemplates();
  }, [selectedLanguage]);

  useEffect(() => {
    if (generationRequest !== null) {
      const { request, template } = generationRequest;
      const orientation = returnKeyByValueType(ORIENTATIONS, request.orientation);
      orientation && changeOrientation(orientation);
      const language = returnKeyByValueType(LANGUAGES, template.language);
      language && setSelectedLanguage(language);
      const voiceTone = returnKeyByValueType(VOICE_TONES, request.voiceTone);
      voiceTone && setSelectedVoiceTone(voiceTone);
      setSelectedTemplateId(template.id);
      const numberOfActors = returnKeyByValueType(NUMBERS_OF_ACTORS, request.numberOfActors);
      numberOfActors && setNumberOfActors(numberOfActors);
      const numberOfScenes = returnKeyByValueType(scenesNumberOptions, request.numberOfScenes ?? DEFAULT_SCENES);
      numberOfScenes && setNumberOfScenes(numberOfScenes);
      setTemplateAnswers(request.fields);
      request.fields.length && setIsShowQuestions(true);
    }
  }, [generationRequest]);

  return (
    <>
      <div className={styles.headerContainer}>
        <div className={cx(styles.header, { [styles.scroll]: scrolled })}>
          <CustomButton onClick={() => history.goBack()} variant="link" className={styles.backButton}>
            <Icon src="arrowLeftStoryboard" className={styles.leftArrowIcon} />
            <span className={styles.btnLabel}>{t('general.back')}</span>
          </CustomButton>
          <div className={styles.btnContainer}>
            {!(generationRequest || isExistingStory) && (
              <CustomButton onClick={skipGenerateNarratives} variant="link" className={styles.skipBtn}>
                <span>{t('storyboard.header.skip_to_storyboard')}</span>
              </CustomButton>
            )}
            <CustomButton
              disabled={!isValid}
              label={t('storyboard.editor.next')}
              onClick={handleNextStep}
              variant="basic"
              className={styles.nextBtn}
            />
          </div>
        </div>
      </div>
      <div className={styles.wrapper}>
        <NameEditComponent size="small" name={projectName} handleSaveName={updateProjectName} isEditIcon={true} />
        <div className={styles.section}>
          <TextWithInfo
            text={t('storyboard.generating.select_orientation')}
            // infoText={t('storyboard.generating.select_orientation.tooltip')}
            className={styles.orientationText}
          />
          <div className={styles.orientationWrapper}>
            {Object.values(ORIENTATIONS).map(orientation => (
              <div
                key={orientation?.type}
                className={cx(styles.orientationItem, {
                  [styles.selectedOrientationItem]: orientation?.type === selectedOrientation.type,
                })}
                onClick={() => changeOrientation(orientation)}
              >
                <Icon src={`${orientation.type}Storyboard`} className={styles.orientaionIcon} />
                <span>{t(orientation.text)}</span>
                <Icon
                  src={orientation.type === selectedOrientation.type ? 'storyboardCheckboxEnabled' : 'storyboardCheckboxDisabled'}
                />
              </div>
            ))}
          </div>
        </div>
        <div className={styles.section}>
          <SelectOption
            title={t('storyboard.generating.select_language')}
            currentlySelected={selectedLanguage}
            options={Object.values(LANGUAGES)}
            onSelect={handleSelectLanguage}
            dropdownClassName={styles.customSelect}
          />
        </div>
        {/* <div className={styles.section}>
          <span className={styles.voiceToneTitle}>{t('storyboard.generating.select_voice_tone')}</span>
          <div className={styles.voiceToneWrapper}>
            {Object.values(VOICE_TONES).map(voiceTone => (
              <div
                key={voiceTone?.type}
                className={cx(styles.voiceToneItem, { [styles.selectedVoiceToneItem]: voiceTone?.type === selectedVoiceTone.type })}
                onClick={() => setSelectedVoiceTone(voiceTone)}
              >
                <span className={styles.voiceToneText}>{t(voiceTone.text)}</span>
                <Icon src={voiceTone?.type === selectedVoiceTone.type ? 'storyboardCheckboxEnabled' : 'storyboardCheckboxDisabled'} />
              </div>
            ))}
          </div>
        </div> */}
        {isTemplatesLoading ? (
          <Spinner width="40px" height="40px" minWidth="40px" minHeight="40px" size="4px" className={styles.spinner} />
        ) : (
          <div className={styles.section}>
            {/*
            <span className={styles.categoryHeader}>{t('storyboard.generating.category')}</span>
            <span className={styles.categoryTitle}>{t('storyboard.generating.select_category')}</span>
            <div className={styles.categoryWrapper}>
              {availableCategories.map(category => (
                <div
                  key={category?.id}
                  className={cx(styles.categoryItem, { [styles.selectedCategoryItem]: category?.id === selectedCategoryId })}
                  onClick={() => setSelectedCategoryId(category?.id)}
                >
                  <span className={styles.categoryName}>{t(category?.key)}</span>
                </div>
              ))}
            </div>
            */}
            <span className={styles.categoryTitle}>{t('storyboard.generating.select_template')}</span>
            <div className={styles.templatesWrapper} ref={templatesWrapper}>
              {templatesToDisplay?.length ? (
                templatesToDisplay.map(template => (
                  <TemplateItem
                    key={template?.id}
                    template={template}
                    selectedTemplateId={selectedTemplateId}
                    handleSelectTemplate={handleSelectTemplate}
                    templatesWrapper={templatesWrapper}
                  ></TemplateItem>
                ))
              ) : (
                <div className={styles.noTemplates}>{t('storyboard.generating.no_templates')}</div>
              )}
            </div>
          </div>
        )}
        <div className={styles.advancedOptionsContainer} onClick={handleToggleAdvancedOpened}>
          <span>
            {isAdvancedOpened
              ? t('company.settings.subtitle_style.hide_advanced_settings')
              : t('company.settings.subtitle_style.show_advanced_settings')}
          </span>
          <Icon src="arrowDown" alt="" className={cx(styles.arrowButton, { [styles.turnArrow]: isAdvancedOpened })} />
        </div>
        {isAdvancedOpened && (
          <>
            <div className={styles.section}>
              <SelectOption
                title={t('storyboard.generating.select_scenes_number')}
                currentlySelected={numberOfScenes}
                options={Object.values(scenesNumberOptions)}
                onSelect={value => setNumberOfScenes(value)}
                dropdownClassName={styles.customSelect}
              />
            </div>
            {/* <div className={styles.section}>
              <SelectOption
                title={t('storyboard.generating.select_number_of_actors')}
                currentlySelected={numberOfActors}
                options={Object.values(NUMBERS_OF_ACTORS)}
                onSelect={value => setNumberOfActors(value)}
                dropdownClassName={styles.customSelect}
              />
            </div> */}
          </>
        )}
        <Modal className={styles.modal} open={isShowQuestions} onClose={() => setIsShowQuestions(false)}>
          <div className={styles.modalContainer}>
            <div className={styles.headerModal}>
              <div className={styles.modalTitleContainer}>
                <span className={styles.modalTitle}>
                  {t('storyboard.generating.modal_title', { language: t(`storyboard.generating.${selectedLanguage.type}_language`) })}
                </span>
                <span className={styles.modalText}>{t('storyboard.generating.modal_text')}</span>
              </div>
              <CloseButton onClose={() => setIsShowQuestions(false)} />
            </div>
            <div className={styles.modalQuestionContainer}>
              {questionsToDisplay.map(question => {
                const { answer = '' } = templateAnswers.find(templateAnswer => templateAnswer.id === question?.id) || {};

                return (
                  <div key={question?.id} className={styles.modalQuestionItem}>
                    <div className={styles.modalQuestionTitleContainer}>
                      <span className={styles.modalQuestionTitle}>{question.title}</span>
                      <span className={styles.answerCount}>
                        {answer.length}/{MAX_ANSWER_LENGTH}
                      </span>
                    </div>
                    <textarea
                      value={answer}
                      onChange={event => handleChangeAnswer(event, question.id)}
                      type="text"
                      className={cx(styles.answerTextarea, {
                        [styles.answerTextareaInvalid]: answer.length <= MIN_ANSWER_LENGTH && touchedAnswers.includes(question.id),
                      })}
                      placeholder={t('storyboard.generating.modal_question_placeholder', {
                        language: t(`storyboard.generating.${selectedLanguage.type}_language`),
                      })}
                      maxLength={MAX_ANSWER_LENGTH}
                    />
                  </div>
                );
              })}
            </div>
            <CustomButton
              disabled={!isTemplateAnswersValid}
              label={t('storyboard.generating.modal_generate_script')}
              variant="primary"
              onClick={onGenerate}
              className={styles.button}
            />
          </div>
        </Modal>
      </div>
    </>
  );
};

export default NewStoryboardCreator;
